/**
 * LAP SIDING (2026-10-05) — the project's siding settings, stored once in the settings
 * map (Matt chose PROJECT-WIDE over per-face or duplicate materials). The material is the
 * product (board, exposure, thickness); these are how it's installed: outside corners
 * finished with a corner TRIM BOARD (5/4 x 4 by default) or MITERED courses.
 *
 * Read by geometry through the resolved wall: doc.ts folds them into each lap siding's
 * resolved CladdingSpec (`corner`, `cornerBoardWidthFt`, `cornerBoardThickFt`), so the
 * generators never need a model handle (the `structural` precedent).
 */
import { maps, transact, type ModelDoc } from './doc'

export interface SidingSettings {
  corner: 'trim' | 'miter'
  /** corner board face width + thickness, feet (5/4 x 4 = 1" x 3-1/2") */
  cornerBoardWidthFt: number
  cornerBoardThickFt: number
}

const KEY = 'siding'
export const SIDING_DEFAULTS: SidingSettings = { corner: 'trim', cornerBoardWidthFt: 3.5 / 12, cornerBoardThickFt: 1 / 12 }

export function normalizeSidingSettings(v: unknown): SidingSettings {
  const o = (v || {}) as Partial<SidingSettings>
  const w = Number(o.cornerBoardWidthFt), t = Number(o.cornerBoardThickFt)
  return {
    corner: o.corner === 'miter' ? 'miter' : 'trim',
    cornerBoardWidthFt: w >= 1 / 12 && w <= 1 ? w : SIDING_DEFAULTS.cornerBoardWidthFt,
    cornerBoardThickFt: t >= 0.5 / 12 && t <= 3 / 12 ? t : SIDING_DEFAULTS.cornerBoardThickFt,
  }
}

export function getSidingSettings(model: ModelDoc): SidingSettings {
  return normalizeSidingSettings(maps(model).settings.get(KEY))
}

/** Patch-merge the siding settings. One transact = one undo step. */
export function setSidingSettings(model: ModelDoc, patch: Partial<SidingSettings>): void {
  transact(model, () => {
    maps(model).settings.set(KEY, { ...getSidingSettings(model), ...patch })
  })
}
